import React from 'react';
import {
    Box,
    FormControl,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    Typography,
} from '@mui/material';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import { useTranslation } from 'react-i18next';

type AuthorityItem = {
    id: string;
    titleId: number | '';
    employeeId: number | '';
};

type GrpcAuthorityRowProps = {
    authority: AuthorityItem;
    employeeTitles: Array<{ id: number; key: string }>;
    authorityEmployees: Array<{ id: number; name: string }>;
    canManageAuthorities: boolean;
    onUpdate: (id: string, changes: Partial<AuthorityItem>) => void;
    onRemove: (id: string) => void;
};

/** Extracted so Select onChange handlers are not nested under authorities.map (S2004). */
export const GrpcAuthorityRow: React.FC<GrpcAuthorityRowProps> = ({
    authority,
    employeeTitles,
    authorityEmployees,
    canManageAuthorities,
    onUpdate,
    onRemove,
}) => {
    const { t } = useTranslation();

    const handleTitleChange = (value: number | '') => {
        onUpdate(authority.id, { titleId: value as number, employeeId: '' });
    };

    const handleEmployeeChange = (value: number | '') => {
        onUpdate(authority.id, { employeeId: value as number });
    };

    return (
        <Box
            sx={{
                display: 'flex',
                gap: 2,
                alignItems: 'center',
                flexWrap: 'wrap',
            }}
        >
            <FormControl fullWidth size="small" sx={{ flex: 1, minWidth: 220 }}>
                <InputLabel>{t('studio.grpcFileManager.role')}</InputLabel>
                <Select
                    value={authority.titleId}
                    label={t('studio.grpcFileManager.role')}
                    disabled={!canManageAuthorities}
                    onChange={(event) => handleTitleChange(event.target.value as number)}
                >
                    <MenuItem value="">
                        <em>{t('general.select')}</em>
                    </MenuItem>
                    {employeeTitles.map((title) => (
                        <MenuItem key={title.id} value={title.id}>
                            {title.key}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
            <FormControl fullWidth size="small" sx={{ flex: 1, minWidth: 220 }}>
                <InputLabel>{t('studio.grpcFileManager.person')}</InputLabel>
                <Select
                    value={authority.employeeId}
                    label={t('studio.grpcFileManager.person')}
                    disabled={!canManageAuthorities || !authority.titleId}
                    onChange={(event) => handleEmployeeChange(event.target.value as number)}
                >
                    <MenuItem value="">
                        <em>{t('general.select')}</em>
                    </MenuItem>
                    {authorityEmployees.map((employee) => (
                        <MenuItem key={employee.id} value={employee.id}>
                            {employee.name}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
            <IconButton
                onClick={() => onRemove(authority.id)}
                color="error"
                disabled={!canManageAuthorities}
                sx={{
                    '& .MuiSvgIcon-root': {
                        height: '18px',
                        width: '18px',
                    },
                }}
            >
                <RemoveCircleIcon />
            </IconButton>
        </Box>
    );
};

type GrpcAddAuthorityControlProps = {
    canManageAuthorities: boolean;
    onAdd: () => void;
};

export const GrpcAddAuthorityControl: React.FC<GrpcAddAuthorityControlProps> = ({
    canManageAuthorities,
    onAdd,
}) => {
    const { t } = useTranslation();

    return (
        <FormControl sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
            <IconButton
                onClick={onAdd}
                color="primary"
                disabled={!canManageAuthorities}
                sx={{
                    '& .MuiSvgIcon-root': {
                        height: '18px',
                        width: '18px',
                    },
                }}
            >
                <AddCircleIcon />
            </IconButton>
            <Typography
                onClick={canManageAuthorities ? onAdd : undefined}
                sx={{
                    ml: 1,
                    fontWeight: 'bold',
                    cursor: canManageAuthorities ? 'pointer' : 'not-allowed',
                    color: canManageAuthorities ? 'text.primary' : 'text.disabled',
                }}
            >
                {t('studio.grpcFileManager.addAuthority')}
            </Typography>
        </FormControl>
    );
};
